import { FC, useEffect, useState } from 'react'

// Libs
import { cloneDeep, sumBy } from 'lodash'

// Components
import ConfirmationCompsWrapper from './ConfirmationCompsWrapper'
import { SelectedEventPrice } from './SelectedEventPrice'

import { useAppSelector } from '../../../../../../store/hooks'
import {
  selectRegistration,
  selectRegistrationFeesR,
  selectTeamTabData,
} from '../../../../../../store/registration/registrationSlice'

import { getFloatPrice } from '../../../../../../helpers/price'
import { getTeamMemberById } from '../../../../../../helpers/teamMembers'

import useRegistrationFees from '../../../../../../hooks/useRegistrationFees'

// Types
import { IRegistrationEventTab } from '../../../../../../types/competitor_types'
import { IUserInterface } from '../../../../../../models/users/user.interface'
import { IRegistrationFeesInterface } from '../../../../../../models/registration-fees/registrationFees.interface'

import { FEES_CATEGORY_CONST } from '../../../../../../components/events/views/details/EventDetailsViewComponentFees'
import { CONST } from '../../../../../../const/const'

type SelectedEventFeeListProps = {
  type: 'fees' | 'mandatory-fees'
  setEventTab: (eventTab: IRegistrationEventTab) => void
  isSplitBill: boolean
  registrar: IUserInterface | null
  currency: string
}

const SelectedEventFeeList: FC<SelectedEventFeeListProps> = ({
  type,
  setEventTab,
  isSplitBill,
  registrar,
  currency,
}: SelectedEventFeeListProps) => {
  const { onUpdateFees } = useRegistrationFees()

  const registrationFees = useAppSelector(selectRegistrationFeesR)
  const teamTabData = useAppSelector(selectTeamTabData)
  const registration = useAppSelector(selectRegistration)

  const [open, setOpen] = useState(false)
  const [selectedUnits, setSelectedUnits] = useState<{
    totalItems: number
    totalPrice: number
  }>({ totalItems: 0, totalPrice: 0 })
  const [allFees, setAllFees] = useState<IRegistrationFeesInterface[]>([])
  const [isSendBillToAllChecked, setIsSendBillToAllChecked] = useState(false)

  useEffect(() => {
    let fees = cloneDeep(registrationFees)
    if (type === 'fees') {
      fees = fees.filter((fee) => fee.feesItemCategory !== FEES_CATEGORY_CONST.GOVERNANCE)
    } else if (type === 'mandatory-fees') {
      fees = fees.filter((fee) => fee.feesItemCategory === FEES_CATEGORY_CONST.GOVERNANCE)
    }

    const totalPrice = parseFloat(sumBy(fees, 'feesPrice').toFixed(2))
    setSelectedUnits({
      totalItems: fees.length,
      totalPrice,
    })

    setAllFees(fees)
  }, [registrationFees, type])

  const onAssignFeesChange = async (item: IRegistrationFeesInterface, id: string) => {
    if (!registrar) return

    const teamMember = getTeamMemberById(registrar, teamTabData, id)

    const registrationFeesToUpdate = [
      {
        ...item,
        ownerId: teamMember?.memberId || null,
        ownerName: teamMember?.memberName || null,
        recipientId: registration?.userId || null,
        recipientName: registration?.userName || null,
        recipientProfilePicture: registration?.userProfilePicture || null,
      },
    ]
    await onUpdateFees({
      registrationFeesToUpdate,
    })
  }

  const onRecipientChange = async (item: IRegistrationFeesInterface, isChecked: boolean) => {
    if (!item?.ownerId || !registrar) return

    setIsSendBillToAllChecked(false)

    const teamMember = getTeamMemberById(registrar, teamTabData, item?.ownerId)

    const registrationFeesToUpdate = [
      {
        ...item,
        recipientId: (isChecked ? teamMember?.memberId : registration?.userId) || null,
        recipientName: (isChecked ? teamMember?.memberName : registration?.userName) || null,
        recipientProfilePicture:
          (isChecked ? teamMember?.memberProfilePicture : registration?.userProfilePicture) || null,
      },
    ]

    await onUpdateFees({
      registrationFeesToUpdate,
    })
  }

  const onSendBillToAll = async (isChecked: boolean) => {
    if (!registrar || !registration) return

    setIsSendBillToAllChecked(!isSendBillToAllChecked)

    let registrationFeesToUpdate = cloneDeep(registrationFees)

    registrationFeesToUpdate = registrationFeesToUpdate.filter((item) =>
      type === 'mandatory-fees'
        ? item.feesItemCategory === FEES_CATEGORY_CONST.GOVERNANCE
        : item.feesItemCategory !== FEES_CATEGORY_CONST.GOVERNANCE
    )
    registrationFeesToUpdate = registrationFeesToUpdate.map((item) => {
      if (!item?.ownerId || !registrar) return item

      const teamMember = getTeamMemberById(registrar, teamTabData, item?.ownerId)

      return {
        ...item,
        recipientId: isChecked ? teamMember?.memberId || null : registration?.userId || null,
        recipientName: isChecked ? teamMember?.memberName || null : registration?.userName || null,
        recipientProfilePicture: isChecked
          ? teamMember?.memberProfilePicture || null
          : registration?.userProfilePicture || null,
      }
    })

    await onUpdateFees({
      registrationFeesToUpdate,
    })
  }

  // Group by feesItemId
  const groupedFees = allFees.reduce<Record<string, IRegistrationFeesInterface[]>>((acc, fee) => {
    if (!acc[fee.feesItemId || '']) acc[fee.feesItemId || ''] = []
    acc[fee.feesItemId || ''].push(fee)
    return acc
  }, {})

  return (
    <ConfirmationCompsWrapper
      title={type === 'fees' ? 'Fees' : 'Mandatory Fees'}
      setEventTab={setEventTab}
      redirectTab={CONST.UI.REGISTER.TABS.FEES}
      selected_units={`${selectedUnits.totalItems} ${selectedUnits.totalItems > 1 ? 'fees' : 'fee'}, ${getFloatPrice(`${currency}${selectedUnits.totalPrice}`)}`}
      accordion={{ open, setOpen }}
      showList={!!allFees.length}
      isSplitBill={isSplitBill}
      isSendBillToAllChecked={isSendBillToAllChecked}
      onSendBillToAll={onSendBillToAll}
    >
      {Object.entries(groupedFees).map(([_, items]) =>
        items.map((item, index) => (
          <div
            key={index}
            className="w-full flex flex-wrap flex-col sm:flex-row gap-3 sm:items-center sm:justify-between p-4 hover:bg-gray-50 transition-colors border-b border-gray-100"
          >
            <div>
              <p className="font-medium text-gray-900">{item.feesTitle}</p>
              <p className="text-sm text-gray-600">
                {item.feesItemCategory === FEES_CATEGORY_CONST.GOVERNANCE
                  ? registration?.isSplit
                    ? `Horse: ${item.horseName} | Rider: ${item.ownerName}`
                    : `Horse: ${item.horseName}`
                  : `Item ${index + 1} of ${items.length}`}
              </p>
            </div>
            <SelectedEventPrice
              onRecipientChange={(state) => onRecipientChange(item, state)}
              recipientId={item?.recipientId || null}
              onAssignChange={(id) => onAssignFeesChange(item, id)}
              ownerId={item?.ownerId || null}
              price={getFloatPrice(`${currency}${item.feesPrice}`)}
              isSplitBill={isSplitBill}
              showAssignTo={item.feesItemCategory !== FEES_CATEGORY_CONST.GOVERNANCE}
            />
          </div>
        ))
      )}
    </ConfirmationCompsWrapper>
  )
}

export default SelectedEventFeeList
